/* eslint-disable camelcase */

import {
  FeedbackAlertItem,
  Role,
  UserConfiguration,
  DeviceType,
  FormItemType,
  RatingType,
  Device,
  DashboardFilters
} from '@/utils/DataTypes'
import { getRandomUid } from '@/utils/HelperFunctions'
import { defineStore } from 'pinia'
import type { KeycloakProfile } from 'keycloak-js'

interface AppState {
  accessToken: string | null
  sessionInitialized: boolean
  userUuid: string
  username: string
  fullname: string
  company_name: string
  email: string
  role_id: string
  permissions: string[]
  systemRoleList: Role[]
  ratingTypesList: RatingType[]
  deviceTypeList: DeviceType[]
  formItemTypesList: FormItemType[]
  alerts: FeedbackAlertItem[]
  userProfile: KeycloakProfile | null,
  deviceList: Device[],
  dashboardFilters: DashboardFilters
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    accessToken: null,
    sessionInitialized: false,
    userProfile: null,
    userUuid: 'N/A',
    username: 'N/A',
    fullname: 'N/A',
    company_name: 'N/A',
    email: 'N/A',
    role_id: 'N/A',
    permissions: [],
    systemRoleList: [],
    ratingTypesList: [],
    deviceTypeList: [],
    formItemTypesList: [],
    alerts: [],
    deviceList: [],
    dashboardFilters: {
      searchFormQuery: '',
      selectedDateRange: null,
      selectedDeviceIds: []
    }
  }),
  getters: {
    isSessionInitialized: (state): boolean => state.sessionInitialized,

    getAppState (): AppState {
      return {
        accessToken: this.accessToken,
        sessionInitialized: this.sessionInitialized,
        userProfile: this.userProfile,
        userUuid: this.userUuid,
        username: this.username,
        fullname: this.fullname,
        company_name: this.company_name,
        email: this.email,
        role_id: this.role_id,
        permissions: this.permissions,
        systemRoleList: this.systemRoleList || [],
        ratingTypesList: this.ratingTypesList,
        deviceTypeList: this.deviceTypeList,
        formItemTypesList: this.formItemTypesList,
        alerts: this.alerts,
        deviceList: this.deviceList,
        dashboardFilters: this.dashboardFilters
      }
    }
  },
  actions: {
    setConfig (user: UserConfiguration): void {
      this.username = user.username
      this.fullname = `${user.first_name} ${user.last_name}`
      this.company_name = user.company_name
      this.role_id = user.role_id
      this.permissions = user.permissions
      this.systemRoleList = user.systemRoleList
    },

    setSession (newAccessToken: string | null | undefined, newUserProfile: KeycloakProfile | null = null) {
      if (!newAccessToken) {
        this.clearSession()
        return
      }
      this.accessToken = newAccessToken
      this.userProfile = newUserProfile
      this.sessionInitialized = true
    },

    clearSession (): void {
      this.accessToken = null
      this.userUuid = 'N/A'
      this.username = 'N/A'
      this.fullname = 'N/A'
      this.company_name = 'N/A'
      this.email = 'N/A'
      this.sessionInitialized = false
      this.role_id = 'N/A'
      this.permissions = []
      this.systemRoleList = []
      this.alerts = []
      this.userProfile = null
      this.deviceList = []
      this.dashboardFilters = {
        searchFormQuery: '',
        selectedDateRange: null,
        selectedDeviceIds: []
      }
    },

    setRatingTypes (ratingTypesList: RatingType[]): void {
      ratingTypesList.forEach(ratingType => {
        ratingType.translationKey = `i18n.${ratingType.name}`
      })
      this.ratingTypesList = ratingTypesList
    },

    setDeviceTypes (deviceTypeList: DeviceType[]): void {
      this.deviceTypeList = deviceTypeList
    },

    setFormItemTypes (formItemTypesList: FormItemType[]): void {
      this.formItemTypesList = formItemTypesList
    },

    setDeviceList (deviceList: Device[]): void {
      this.deviceList = deviceList
    },

    setDashboardFilters (filters: DashboardFilters): void {
      this.dashboardFilters = filters
    },

    addAlert (
      type: 'success' | 'warning' | 'error' | 'info',
      message: string
    ): void {
      const alertId = getRandomUid()

      const alert: FeedbackAlertItem = {
        id: alertId,
        text: message,
        feedbackType: type
      }

      this.alerts.unshift(alert)

      setTimeout(() => {
        this.removeAlert(alertId)
      }, 10000)
    },

    removeAlert (alertId: string): void {
      this.alerts = this.alerts.filter(alert => alert.id !== alertId)
    }
  }
})
